import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import Layout from '@/components/Layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { useI18n } from '@/hooks/useI18n';
import { apiFetch } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface Currency {
    id: string;
    code: string;
    name: string;
    symbol?: string;
    is_active: boolean;
    created_at: string;
    updated_at?: string;
}

export default function CurrenciesPage() {
    const navigate = useNavigate();
    const { t, isReady } = useI18n();

    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [baseCurrency, setBaseCurrency] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updatingBaseCurrency, setUpdatingBaseCurrency] = useState<string | null>(null);
    const [updatingStatusCurrency, setUpdatingStatusCurrency] = useState<string | null>(null);

    useEffect(() => {
        if (!isReady) {
            return;
        }
        void fetchData();
    }, [isReady]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [currenciesResponse, baseCurrencyResponse] = await Promise.all([
                apiFetch('/api/currencies/'),
                apiFetch('/api/currencies/base-currency'),
            ]);

            if (!currenciesResponse.ok || !baseCurrencyResponse.ok) {
                setError(t('currencies.loadFailed', 'admin'));
                return;
            }

            const currenciesData = await currenciesResponse.json();
            const baseCurrencyData = await baseCurrencyResponse.json();

            setCurrencies(Array.isArray(currenciesData) ? currenciesData : []);
            setBaseCurrency(baseCurrencyData.base_currency || '');
        } catch (err) {
            setError(t('currencies.loadFailed', 'admin'));
            console.error('Error fetching currencies:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSetBaseCurrency = async (currencyCode: string) => {
        try {
            setUpdatingBaseCurrency(currencyCode);
            setError(null);
            const response = await apiFetch('/api/currencies/base-currency', {
                method: 'PATCH',
                body: JSON.stringify({ currency_code: currencyCode }),
            });

            if (!response.ok) {
                setError(t('currencies.setBaseFailed', 'admin'));
                return;
            }

            await fetchData();
        } catch (err) {
            setError(t('currencies.setBaseFailed', 'admin'));
            console.error('Error setting base currency:', err);
        } finally {
            setUpdatingBaseCurrency(null);
        }
    };

    const handleToggleActive = async (currencyId: string, isActive: boolean) => {
        try {
            setUpdatingStatusCurrency(currencyId);
            setError(null);
            const response = await apiFetch(`/api/currencies/${currencyId}`, {
                method: 'PUT',
                body: JSON.stringify({ is_active: !isActive }),
            });

            if (!response.ok) {
                setError(t('currencies.updateStatusFailed', 'admin'));
                return;
            }

            await fetchData();
        } catch (err) {
            setError(t('currencies.updateStatusFailed', 'admin'));
            console.error('Error updating currency:', err);
        } finally {
            setUpdatingStatusCurrency(null);
        }
    };

    if (!isReady || loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg">{t('actions.loading', 'common')}</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">
                            {t('currencies.title', 'finance')}
                        </h1>
                        <p className="text-gray-600">
                            {t('currencies.description', 'finance')}
                        </p>
                    </div>
                    <Button onClick={() => navigate({ to: '/admin/currencies/new' as any })}>
                        {t('actions.create', 'common')} {t('currencies.currency', 'finance')}
                    </Button>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription className="flex items-center justify-between gap-3">
                            <span>{error}</span>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-auto px-2 py-1 text-destructive hover:text-destructive"
                                onClick={() => setError(null)}
                            >
                                {t('actions.close', 'common') || 'Dismiss'}
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}

                <div className="border rounded-lg overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('currencies.code', 'finance')}</TableHead>
                                <TableHead>{t('currencies.name', 'finance')}</TableHead>
                                <TableHead>{t('currencies.symbol', 'finance')}</TableHead>
                                <TableHead>{t('currencies.baseCurrency', 'finance')}</TableHead>
                                <TableHead>{t('status.active', 'common')}</TableHead>
                                <TableHead>{t('currencies.createdAt', 'finance')}</TableHead>
                                <TableHead className="text-right">{t('actions.actions', 'common')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {currencies.map((currency) => (
                                <TableRow key={currency.id}>
                                    <TableCell className="font-medium">{currency.code}</TableCell>
                                    <TableCell>{currency.name}</TableCell>
                                    <TableCell>{currency.symbol || '-'}</TableCell>
                                    <TableCell>
                                        {currency.code === baseCurrency ? (
                                            <Badge>{t('currencies.baseCurrency', 'finance')}</Badge>
                                        ) : (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                disabled={updatingBaseCurrency === currency.code}
                                                onClick={() => handleSetBaseCurrency(currency.code)}
                                            >
                                                {updatingBaseCurrency === currency.code
                                                    ? t('actions.saving', 'common')
                                                    : t('currencies.setAsBase', 'finance')}
                                            </Button>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant={currency.is_active ? 'default' : 'secondary'}
                                            size="sm"
                                            disabled={updatingStatusCurrency === currency.id}
                                            onClick={() => handleToggleActive(currency.id, currency.is_active)}
                                        >
                                            {updatingStatusCurrency === currency.id
                                                ? t('actions.saving', 'common')
                                                : currency.is_active
                                                    ? t('status.active', 'common')
                                                    : t('status.inactive', 'common')}
                                        </Button>
                                    </TableCell>
                                    <TableCell>{formatDate(currency.created_at)}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => navigate({ to: `/admin/currencies/${currency.id}` })}
                                            >
                                                {t('actions.edit', 'common')}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => navigate({ to: `/admin/currencies/${currency.id}/exchange-rates` })}
                                            >
                                                {t('currencies.exchangeRates', 'finance')}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {currencies.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                                        {t('messages.noData', 'common')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/admin/currencies')({
    component: CurrenciesPage,
});
